import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/Button';
import { CATEGORY_LABELS, SERVICE_CATEGORIES } from '@/constants/serviceCategories';
import {
  ServiceRegisterInput,
  ServiceRegisterResult,
  serviceRegistrationAPI,
} from '@/services/apis/serviceRegistrationAPI';
import { getErrorMessage } from '@/utils/errorUtils';

/**
 * 서비스 등록 (#136)
 *
 * 한 화면으로 두 역할을 받는다 — 폼이 갈라지면 한쪽만 고치는 일이 생긴다.
 *
 * - `role="user"`  사용자가 신청한다. 언제나 등록대기로 남고 슈퍼관리자가 승인한다
 * - `role="admin"` 슈퍼관리자가 등록한다. 바로 승인할지 고를 수 있다
 */

interface ServiceRegisterDialogProps {
  open: boolean;
  role: 'user' | 'admin';
  onClose: () => void;
  /** 등록에 성공하면 목록을 다시 읽도록 알린다 */
  onRegistered?: (result: ServiceRegisterResult) => void;
}

const EMPTY: ServiceRegisterInput = {
  service_name: '',
  base_slug: '',
  version: '1.0.0',
  service_url: '',
  service_description: '',
  category: '',
  contact_email: '',
  contact_name: '',
  is_public: false,
};

/** 이름에서 식별자를 거들어 준다. 사용자가 고칠 수 있고, 검사는 서버가 다시 한다 */
const suggestSlug = (name: string): string =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const ServiceRegisterDialog: React.FC<ServiceRegisterDialogProps> = ({
  open,
  role,
  onClose,
  onRegistered,
}) => {
  const [form, setForm] = useState<ServiceRegisterInput>(EMPTY);
  const [approveNow, setApproveNow] = useState(true);
  const [slugTouched, setSlugTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<ServiceRegisterResult | null>(null);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setApproveNow(true);
      setSlugTouched(false);
      setError('');
      setResult(null);
    }
  }, [open]);

  if (!open) return null;

  const set = <K extends keyof ServiceRegisterInput>(
    key: K,
    value: ServiceRegisterInput[K],
  ) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleNameChange = (name: string) => {
    setForm((prev) => ({
      ...prev,
      service_name: name,
      base_slug: slugTouched ? prev.base_slug : suggestSlug(name),
    }));
  };

  const ready = !!form.service_name.trim() && !!form.base_slug.trim() && !!form.service_url.trim();

  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    try {
      const payload: ServiceRegisterInput = {
        ...form,
        service_description: form.service_description || undefined,
        category: form.category || undefined,
        contact_email: form.contact_email || undefined,
        contact_name: form.contact_name || undefined,
      };
      const created =
        role === 'admin'
          ? await serviceRegistrationAPI.register(payload, approveNow)
          : await serviceRegistrationAPI.submit(payload);
      setResult(created);
      onRegistered?.(created);
    } catch (e) {
      setError(getErrorMessage(e, '등록하지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-1">
          {role === 'admin' ? '서비스 등록' : '서비스 등록 신청'}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {role === 'admin'
            ? '서비스를 직접 등록합니다. 바로 승인할 수도 있고, 등록대기로 남길 수도 있습니다.'
            : '내 서비스를 목록에 올립니다. 신청은 등록대기로 남고 운영자가 확인한 뒤 승인합니다.'}
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}

        {result ? (
          <>
            <div className="mb-4 p-4 bg-green-50 border border-green-300 rounded text-sm">
              <p className="font-semibold text-green-900 mb-1">{result.message}</p>
              <p className="text-green-800">
                식별자 <code className="font-mono">{result.service_slug}</code> · 상태{' '}
                <strong>{result.status}</strong>
              </p>
              {result.status === 'pending' && (
                <p className="text-green-800 mt-2">
                  승인되면 목록에 보입니다. 로그인 연동(client_id)은 승인 뒤에 발급합니다.
                </p>
              )}
            </div>

            {!!result.manifest_warnings?.length && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded text-sm">
                <p className="font-semibold text-amber-900 mb-1">고쳐야 할 것</p>
                <ul className="list-disc pl-5 text-amber-800">
                  {result.manifest_warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex pt-3 border-t">
              <Button onClick={onClose} size="sm" className="ml-auto">
                닫기
              </Button>
            </div>
          </>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  서비스 이름 <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.service_name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="로고 만들기"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  식별자 <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.base_slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set('base_slug', e.target.value);
                  }}
                  className="w-full border rounded p-2 font-mono text-xs"
                  placeholder="logogen"
                />
                <p className="text-xs text-gray-500 mt-1">
                  소문자·숫자·하이픈만. URL 과 내부 이름에 그대로 쓰이므로{' '}
                  <strong>나중에 바꿀 수 없습니다.</strong>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">버전</label>
                <input
                  value={form.version}
                  onChange={(e) => set('version', e.target.value)}
                  className="w-full border rounded p-2 font-mono text-xs"
                  placeholder="1.0.0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  major 가 다르면 <strong>다른 서비스</strong>로 관리됩니다 (1.x → {form.base_slug || 'slug'}
                  -v1).
                </p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  서비스 주소 <span className="text-red-500">*</span>
                </label>
                <input
                  value={form.service_url}
                  onChange={(e) => set('service_url', e.target.value)}
                  className="w-full border rounded p-2 font-mono text-xs"
                  placeholder="https://logogen.example.com"
                />
                <p className="text-xs text-gray-500 mt-1">
                  서비스가 실제로 도는 주소입니다. 이 주소를 그대로 씁니다 — 우리가 지어내지
                  않습니다.
                </p>
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">설명</label>
                <textarea
                  value={form.service_description}
                  onChange={(e) => set('service_description', e.target.value)}
                  rows={2}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="무엇을 해 주는 서비스인지 한두 줄로"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">카테고리</label>
                <select
                  value={form.category}
                  onChange={(e) => set('category', e.target.value)}
                  className="w-full border rounded p-2 text-sm bg-white"
                >
                  <option value="">고르지 않음</option>
                  {SERVICE_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">담당자</label>
                <input
                  value={form.contact_name}
                  onChange={(e) => set('contact_name', e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="이름 또는 팀"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">연락 메일</label>
                <input
                  value={form.contact_email}
                  onChange={(e) => set('contact_email', e.target.value)}
                  className="w-full border rounded p-2 text-sm"
                  placeholder="team@example.com"
                />
              </div>
            </div>

            <div className="mb-4 space-y-2 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => set('is_public', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                공개 — 다른 고객사도 목록에서 찾을 수 있습니다
              </label>

              {role === 'admin' && (
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={approveNow}
                    onChange={(e) => setApproveNow(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300"
                  />
                  바로 승인 — 끄면 등록대기 목록에 남습니다
                </label>
              )}
            </div>

            <div className="flex gap-2 pt-3 border-t">
              <Button onClick={handleSubmit} disabled={loading || !ready} size="sm">
                {role === 'admin' ? '등록' : '신청'}
              </Button>
              <Button onClick={onClose} size="sm" variant="outline" className="ml-auto">
                취소
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceRegisterDialog;
